import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import './styles/adminEvents.css';
import adminHero from '../images/image1.jpg';
import { FaEdit, FaCopy, FaTrash } from 'react-icons/fa';

function AdminEvents() {
  const navigate = useNavigate();
  const { kursusId } = useParams();
  const [events, setEvents] = useState([]);
  const [user, setUser] = useState(null);
  const [kursusNavn, setKursusNavn] = useState("");
  const [newEvent, setNewEvent] = useState({
    navn: '',
    dato: '',
    tid: '',
    lokation: '',
    pris: '',
    maxDeltagere: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [eventTilmeldinger, setEventTilmeldinger] = useState({});
  const [redigerEvent, setRedigerEvent] = useState(null);
  const [visGamleEvents, setVisGamleEvents] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        if (getCleanKursusId()) {
          hentEvents();
          hentKursusNavn();
        }
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [kursusId, navigate]);

  const getCleanKursusId = () => {
    return kursusId ? kursusId.split('-events')[0] : null;
  };

  const hentEvents = async () => {
    const cleanKursusId = getCleanKursusId();
    const q = query(collection(db, 'events'), where('kursusId', '==', cleanKursusId));
    const snapshot = await getDocs(q);
    setEvents(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    await hentTilmeldinger();
  };

  const hentKursusNavn = async () => {
    const cleanKursusId = getCleanKursusId();
    const kursusRef = doc(db, 'kurser', cleanKursusId);
    try {
      const kursusDoc = await getDoc(kursusRef);
      if (kursusDoc.exists()) {
        setKursusNavn(kursusDoc.data().navn);
      }
    } catch (error) {
      console.error("Fejl ved hentning af kursusnavn:", error);
    }
  };

  const hentTilmeldinger = async () => {
    const tilmeldingerMap = {};
    for (const event of events) {
      const q = query(collection(db, 'tilmeldinger'), where('eventId', '==', event.id));
      const snapshot = await getDocs(q);
      const antalTilmeldte = snapshot.docs.reduce((sum, doc) => 
        sum + doc.data().antalDeltagere, 0);
      tilmeldingerMap[event.id] = antalTilmeldte;
    }
    setEventTilmeldinger(tilmeldingerMap);
  };

  useEffect(() => {
    if (events.length > 0) {
      hentTilmeldinger();
    }
  }, [events]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'events'), {
        ...newEvent,
        kursusId: getCleanKursusId()
      });
      setNewEvent({ navn: '', dato: '', tid: '', lokation: '', pris: '', maxDeltagere: '' });
      hentEvents();
      setShowModal(false);
    } catch (error) {
      alert('Fejl ved oprettelse af event: ' + error.message);
    }
  };

  const handleRedigerGem = async (e) => {
    e.preventDefault();
    try {
      const eventRef = doc(db, 'events', redigerEvent.id);
      await updateDoc(eventRef, {
        navn: newEvent.navn,
        dato: newEvent.dato,
        tid: newEvent.tid,
        lokation: newEvent.lokation,
        pris: newEvent.pris,
        maxDeltagere: newEvent.maxDeltagere
      });
      setRedigerEvent(null);
      setNewEvent({ navn: '', dato: '', tid: '', lokation: '', pris: '', maxDeltagere: '' });
      setShowModal(false);
      hentEvents();
    } catch (error) {
      alert('Fejl ved opdatering af event: ' + error.message);
    }
  };

  const handleRediger = (event) => {
    setRedigerEvent(event);
    setNewEvent({
      navn: event.navn,
      dato: event.dato,
      tid: event.tid,
      lokation: event.lokation,
      pris: event.pris,
      maxDeltagere: event.maxDeltagere
    });
    setShowModal(true);
  };

  const handleCopy = (eventToCopy) => {
    setRedigerEvent(null);
    setNewEvent({
      ...eventToCopy,
      navn: `${eventToCopy.navn} (kopi)`
    });
    setShowModal(true);
  };

  const handleDelete = async (eventId) => {
    if (window.confirm('Er du sikker på at du vil slette denne event?')) {
      try {
        await deleteDoc(doc(db, 'events', eventId));
        hentEvents();
      } catch (error) {
        alert('Fejl ved sletning: ' + error.message);
      }
    }
  };

  const navigateToTilmeldinger = (eventId) => {
    const cleanKursusId = getCleanKursusId();
    navigate(`/admin/${cleanKursusId}/tilmeldinger/${eventId}`);
  };

  const filtrerEvents = () => {
    const idag = new Date();
    idag.setHours(0, 0, 0, 0);
    
    return events
      .filter(event => {
        const eventDato = new Date(event.dato);
        if (visGamleEvents) {
          return true; // Vis alle events
        } else {
          return eventDato >= idag; // Vis kun fremtidige events
        }
      })
      .sort((a, b) => {
        // Sortér efter dato
        const datoA = new Date(a.dato);
        const datoB = new Date(b.dato);
        return datoB - datoA;
      });
  };

  if (!user) return null;

  return (
    <>
      <div className="adminevents-hero">
        <div className="adminevents-hero-overlay"></div>
        <img src={adminHero} alt="Administration" className="adminevents-hero-image" />
      </div>

      <div className="adminevents-container">
        <div className="adminevents-header">
          <Link to="/admin" className="adminevents-back-button">
            ← Tilbage til admin
          </Link>
          <h1>{kursusNavn} Events</h1>
        </div>

        <div className="adminevents-controls">
          <label className="adminevents-toggle">
            <span className="adminevents-toggle-label">Vis gamle events</span>
            <div className="adminevents-toggle-switch">
              <input
                type="checkbox"
                checked={visGamleEvents}
                onChange={(e) => setVisGamleEvents(e.target.checked)}
              />
              <span className="adminevents-toggle-slider"></span>
            </div>
          </label>
        </div>

        <div className="adminevents-grid">
          <div 
            className="adminevents-create-card"
            onClick={() => setShowModal(true)}
          >
            <div className="adminevents-create-icon">+</div>
            <div className="adminevents-create-text">Opret ny event</div>
          </div>
          
          {filtrerEvents().map(event => (
            <div key={event.id} className="adminevents-event-card">
              <div className="adminevents-event-header">
                <h3>{event.navn}</h3>
              </div>
              <div className="adminevents-event-content">
                <p><strong>Dato:</strong> {event.dato}</p>
                <p><strong>Tid:</strong> {event.tid}</p>
                <p><strong>Lokation:</strong> {event.lokation}</p>
                <p><strong>Pris:</strong> {event.pris} kr.</p>
                <p><strong>Deltagere:</strong> {eventTilmeldinger[event.id] || 0}/{event.maxDeltagere}</p>
              </div>
              <div className="adminevents-event-actions">
                <button 
                  onClick={() => handleRediger(event)}
                  className="adminevents-action-btn edit"
                  title="Rediger"
                >
                  <FaEdit />
                </button>
                <button 
                  onClick={() => handleCopy(event)}
                  className="adminevents-action-btn copy"
                  title="Kopier"
                >
                  <FaCopy />
                </button>
                <button 
                  onClick={() => handleDelete(event.id)}
                  className="adminevents-action-btn delete"
                  title="Slet"
                >
                  <FaTrash />
                </button>
                <button 
                  onClick={() => navigateToTilmeldinger(event.id)}
                  className="adminevents-action-btn primary"
                >
                  Tilmeldte
                </button>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="adminevents-modal">
            <div className="adminevents-modal-content">
              <span className="adminevents-close" onClick={() => setShowModal(false)}>&times;</span>
              <form onSubmit={redigerEvent ? handleRedigerGem : handleSubmit} className="adminevents-events-form">
                <div className="adminevents-events-form-group">
                  <label>Eventnavn:</label>
                  <input 
                    type="text" 
                    value={newEvent.navn}
                    onChange={(e) => setNewEvent({...newEvent, navn: e.target.value})}
                    required
                  />
                </div>
                <div className="adminevents-events-form-group">
                  <label>Dato:</label>
                  <input 
                    type="date" 
                    value={newEvent.dato}
                    onChange={(e) => setNewEvent({...newEvent, dato: e.target.value})}
                    required
                  />
                </div>

                <div className="adminevents-events-form-group">
                  <label>Tid:</label>
                  <input 
                    type="time" 
                    value={newEvent.tid}
                    onChange={(e) => setNewEvent({...newEvent, tid: e.target.value})}
                    required
                  />
                </div>

                <div className="adminevents-events-form-group">
                  <label>Lokation:</label>
                  <input 
                    type="text" 
                    value={newEvent.lokation}
                    onChange={(e) => setNewEvent({...newEvent, lokation: e.target.value})}
                    required
                  />
                </div>

                <div className="adminevents-events-form-group">
                  <label>Pris:</label>
                  <input 
                    type="number" 
                    value={newEvent.pris}
                    onChange={(e) => setNewEvent({...newEvent, pris: e.target.value})}
                    required
                  />
                </div>

                <div className="adminevents-events-form-group">
                  <label>Max Deltagere:</label>
                  <input 
                    type="number" 
                    value={newEvent.maxDeltagere}
                    onChange={(e) => setNewEvent({...newEvent, maxDeltagere: e.target.value})}
                    required
                  />
                </div>

                <button type="submit" className="adminevents-events-submit-btn">
                  {redigerEvent ? 'Gem ændringer' : 'Opret Event'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminEvents;